/**
 * Horse Icon Component
 * Displays a cute colored horse face icon
 * 
 * Variants:
 * - "simple": Basic horse face
 * - "blaze": Horse face with white blaze marking
 */
import React from "react";
import { Box } from "@mui/material";
import { HorseColor, HORSE_COLOR_HEX } from "./types";

interface HorseIconProps {
  color: HorseColor | string;
  size?: number;
  style?: React.CSSProperties;
  variant?: "simple" | "blaze";
}

function HorseIcon({ color, size = 40, style, variant = "blaze" }: HorseIconProps) {
  // Get color palette for the horse
  const colorPalette = HORSE_COLOR_HEX[color as HorseColor];
  
  // Fallback for legacy or unknown colors
  const faceColor = colorPalette?.face || color || "#8B5A2B";
  const muzzleColor = colorPalette?.muzzle || "#C68642";
  const maneColor = colorPalette?.mane || "#5A3A1B";
  
  // Eye color
  const eyeColor = "#000";

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {variant === "blaze" ? (
        <svg
          width={size}
          height={size}
          viewBox="0 0 64 64"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Left ear */}
          <path d="M22 8l6 16-10-4z" fill={faceColor} />

          {/* Right ear */}
          <path d="M42 8l-6 16 10-4z" fill={faceColor} />

          {/* Head (taller oval) */}
          <ellipse cx="32" cy="38" rx="18" ry="22" fill={faceColor} />

          {/* Blaze (extended to muzzle) */}
          <path
            d="M30 18
               c1-2 3-2 4 0
               v26
               c0 3-4 3-4 0z"
            fill="#FFFFFF"
          />

          {/* Mane */}
          <path d="M18 22c6-10 22-10 28 0l-8 6c-4-3-8-3-12 0z" fill={maneColor} />

          {/* Eyes */}
          <circle cx="26" cy="36" r="2.5" fill={eyeColor} />
          <circle cx="38" cy="36" r="2.5" fill={eyeColor} />

          {/* Snout */}
          <ellipse cx="32" cy="48" rx="10" ry="7" fill={muzzleColor} />
        </svg>
      ) : (
        <svg
          width={size}
          height={size}
          viewBox="0 0 64 64"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Left ear */}
          <path d="M22 8l6 14-10-4z" fill={faceColor} />

          {/* Right ear */}
          <path d="M42 8l-6 14 10-4z" fill={faceColor} />

          {/* Head */}
          <circle cx="32" cy="36" r="20" fill={faceColor} />

          {/* Mane */}
          <path d="M18 20c6-8 22-8 28 0l-8 6c-4-3-8-3-12 0z" fill={maneColor} />

          {/* Eyes */}
          <circle cx="26" cy="34" r="2.5" fill={eyeColor} />
          <circle cx="38" cy="34" r="2.5" fill={eyeColor} />

          {/* Snout */}
          <ellipse cx="32" cy="44" rx="10" ry="7" fill={muzzleColor} />
        </svg>
      )}
    </Box>
  );
}

export default HorseIcon;
